/*
 * Race time zone helpers (pure, Intl only, no store access, no module state).
 *
 * A race may carry `timezone` (IANA name, e.g. "Europe/Helsinki"). Race dates
 * and result timestamps are UTC instants; these helpers render them in the race
 * zone, build the "(10:00 finsk tid)" label for followers in another zone, and
 * convert a datetime-local wall clock typed in race time to a UTC ISO string.
 * Empty or invalid zones fall back to preact-util so output is unchanged.
 */
import util from 'preact-util';

export const ZONE_LABELS = {
    'Europe/Oslo': 'norsk tid',
    'Europe/Helsinki': 'finsk tid',
    'Europe/Stockholm': 'svensk tid',
    'Europe/Copenhagen': 'dansk tid',
    'Europe/London': 'britisk tid',
    UTC: 'UTC',
};

export const TIMEZONE_OPTIONS = [
    ['Europe/Oslo', 'Norge (Europe/Oslo)'],
    ['Europe/Helsinki', 'Finland (Europe/Helsinki)'],
    ['Europe/Stockholm', 'Sverige (Europe/Stockholm)'],
    ['Europe/Copenhagen', 'Danmark (Europe/Copenhagen)'],
    ['Europe/London', 'Storbritannia (Europe/London)'],
    ['UTC', 'UTC'],
];

const pad = n => String(n).padStart(2, '0');

export function isValidTimezone(tz) {
    if (!tz || typeof tz !== 'string') {
        return false;
    }
    if (ZONE_LABELS[tz]) {
        return true;
    }
    try {
        // eslint-disable-next-line no-new
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch (e) {
        return false;
    }
}

export function viewerTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
        return '';
    }
}

export function raceZone(race) {
    const tz = race?.timezone;
    return isValidTimezone(tz) ? tz : '';
}

export function zoneLabel(tz) {
    if (!tz) {
        return '';
    }
    return ZONE_LABELS[tz] || tz;
}

function toDate(input) {
    if (input === undefined || input === null || input === '') {
        return null;
    }
    if (typeof input === 'string' && !/\d{4}-\d{2}-\d{2}/.test(input) && Number.isNaN(Number(input))) {
        return null;
    }
    const d = util.parseInputDate(input);
    if (!d || Number.isNaN(d.getTime())) {
        return null;
    }
    return d;
}

// Wall-clock parts of an instant in `tz`. hourCycle h23 avoids "24:00".
function zoneParts(date, tz) {
    const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = {};
    fmt.formatToParts(date).forEach((p) => {
        if (p.type !== 'literal') {
            parts[p.type] = parseInt(p.value, 10);
        }
    });
    return {
        yy: parts.year,
        mm: parts.month,
        dd: parts.day,
        hh: parts.hour === 24 ? 0 : parts.hour,
        mi: parts.minute,
        ss: parts.second,
    };
}

function fallbackFormat(date, shape, opts) {
    const showSeconds = !!opts.showSeconds;
    if (shape === 'isoDateCompact') {
        return util.isoDateCompact(date, showSeconds, false, opts.dateTimeSep || ' ');
    }
    if (shape === 'time') {
        return util.isoTime(date, showSeconds);
    }
    return util.isoDate(date, showSeconds, false, !!opts.showDateOnly, opts.dateTimeSep || ' ');
}

export function formatInZone(input, tz, shape = 'isoDate', opts = {}) {
    const date = toDate(input);
    if (!date) {
        return 'n/a';
    }
    if (!isValidTimezone(tz)) {
        return fallbackFormat(date, shape, opts);
    }
    const p = zoneParts(date, tz);
    const sep = opts.dateTimeSep || ' ';
    const hhmm = `${pad(p.hh)}:${pad(p.mi)}${opts.showSeconds ? `:${pad(p.ss)}` : ''}`;
    if (shape === 'time') {
        return hhmm;
    }
    if (shape === 'isoDateCompact') {
        return `${p.dd}/${p.mm}${sep}${hhmm}`;
    }
    const ymd = `${p.yy}-${pad(p.mm)}-${pad(p.dd)}`;
    if (opts.showDateOnly) {
        return ymd;
    }
    return `${ymd}${sep}${hhmm}`;
}

export function adminFormat(input, race, shape = 'isoDate', opts = {}) {
    return formatInZone(input, raceZone(race), shape, opts);
}

export function zoneOption(race) {
    const tz = raceZone(race);
    return tz ? { timeZone: tz } : {};
}

export function raceZoneText(input, race, shape = 'time', opts = {}) {
    const tz = raceZone(race);
    if (!tz || !toDate(input)) {
        return '';
    }
    return `${formatInZone(input, tz, shape, opts)} ${zoneLabel(tz)}`;
}

export function raceLocalText(input, race, shape = 'time', opts = {}, viewerTz = viewerTimezone()) {
    const tz = raceZone(race);
    if (!tz || tz === viewerTz) {
        return '';
    }
    return raceZoneText(input, race, shape, opts);
}

export function raceLocalSuffix(input, race, shape = 'time', opts = {}, viewerTz = viewerTimezone()) {
    const text = raceLocalText(input, race, shape, opts, viewerTz);
    return text ? ` (${text})` : '';
}

// Offset (ms) of `tz` at the given instant: wall clock as UTC minus the instant.
function offsetMs(instantMs, tz) {
    const p = zoneParts(new Date(instantMs), tz);
    return Date.UTC(p.yy, p.mm - 1, p.dd, p.hh, p.mi, p.ss) - instantMs;
}

// A wall clock inside the autumn fold (e.g. 03:30 Helsinki on 2026-10-25) always
// resolves to the standard-time occurrence. A stored instant from the earlier,
// summer-time occurrence therefore shifts one hour if loaded and saved unedited.
export function wallClockToIso(value, tz) {
    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(typeof value === 'string' ? value : '');
    if (!m) {
        return '';
    }
    const yy = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    const dd = parseInt(m[3], 10);
    const hh = parseInt(m[4], 10);
    const mi = parseInt(m[5], 10);
    const ss = m[6] ? parseInt(m[6], 10) : 0;
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || hh > 23 || mi > 59 || ss > 59) {
        return '';
    }
    if (!isValidTimezone(tz)) {
        const local = new Date(yy, mm - 1, dd, hh, mi, ss);
        return Number.isNaN(local.getTime()) ? '' : local.toISOString();
    }
    const asUtc = Date.UTC(yy, mm - 1, dd, hh, mi, ss);
    if (Number.isNaN(asUtc)) {
        return '';
    }
    // Two passes so a guess that lands on the other side of a DST switch is corrected.
    let instant = asUtc - offsetMs(asUtc, tz);
    instant = asUtc - offsetMs(instant, tz);
    return new Date(instant).toISOString();
}

export function isoToWallClock(input, tz, showSeconds = true) {
    if (!toDate(input)) {
        return '';
    }
    return formatInZone(input, tz, 'isoDate', { showSeconds, dateTimeSep: 'T' });
}
